/**
 * Shared utility functions for upload components
 */

/**
 * Format a number with thousand separators
 */
export const formatNumber = (num: number, decimals: number = 0): string => {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals
  }).format(num);
};

/**
 * Format currency in CLP
 */
export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Validate file type
 */
export const validateFileType = (file: File, allowedTypes: string[]): boolean => {
  const extension = file.name.split('.').pop()?.toLowerCase();
  return extension ? allowedTypes.includes(`.${extension}`) : false;
};

/**
 * Validate file size (in MB)
 */
export const validateFileSize = (file: File, maxSizeMB: number): boolean => {
  const sizeMB = file.size / (1024 * 1024);
  return sizeMB <= maxSizeMB;
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Get upload progress message translation key based on progress percentage
 */
export const getProgressStepKey = (progress: number): string => {
  if (progress < 10) return 'upload.status.starting';
  if (progress < 30) return 'upload.status.uploading';
  if (progress < 60) return 'upload.status.processing';
  if (progress < 90) return 'upload.status.analyzing';
  return 'upload.status.finalizing';
};

/**
 * Check if validation data indicates success
 */
export const isValidationSuccessful = (validation: any): boolean => {
  if (!validation) return false;
  return validation.summary?.match === true;
};

/**
 * Extract validation summary text
 */
export const getValidationSummary = (validation: any) => {
  if (!validation?.summary) return null;
  return {
    sourceRecords: validation.summary.source_records || 0,
    dbRecords: validation.summary.db_records || 0,
    match: validation.summary.match || false,
  };
};

/**
 * Calculate statistics for numeric data
 */
export const calculateBasicStats = (data: number[]) => {
  if (data.length === 0) return null;

  const validData = data.filter(d => d > 0 && !isNaN(d));
  if (validData.length === 0) return null;

  const sum = validData.reduce((a, b) => a + b, 0);
  const avg = sum / validData.length;
  const max = Math.max(...validData);
  const min = Math.min(...validData);

  // Calculate standard deviation
  const variance = validData.reduce((sum, val) => sum + Math.pow(val - avg, 2), 0) / validData.length;
  const stdDev = Math.sqrt(variance);

  return {
    avg,
    max,
    min,
    sum,
    count: validData.length,
    stdDev,
    volatility: (stdDev / avg) * 100, // Coefficient of variation as %
  };
};

/**
 * Generate chart period labels
 */
export const generateChartLabels = (
  period: 'day' | 'week' | 'month' | 'year',
  locale: string = 'en-US'
) => {
  switch (period) {
    case 'day':
      return Array.from({ length: 24 }, (_, i) => `${i}:00`);

    case 'week':
      return Array.from({ length: 7 }, (_, i) => {
        const date = new Date(2024, 0, i + 1); // Monday-based week
        return date.toLocaleDateString(locale, { weekday: 'short' });
      });

    case 'month':
      return Array.from({ length: 31 }, (_, i) => (i + 1).toString());

    case 'year':
      return Array.from({ length: 12 }, (_, i) => {
        const date = new Date(2024, i, 1);
        return date.toLocaleDateString(locale, { month: 'short' });
      });

    default:
      return [];
  }
};

/**
 * Aggregate time-series data by period
 */
export const aggregateDataByPeriod = (
  data: Array<{ timestamp: string | Date; value: number }>,
  period: 'hour' | 'day' | 'week' | 'month',
  aggregationType: 'sum' | 'avg' | 'max' | 'min' = 'avg'
) => {
  const aggregated: { [key: string]: { sum: number; count: number; max: number; min: number } } = {};

  data.forEach(item => {
    const date = new Date(item.timestamp);
    let key: string;

    switch (period) {
      case 'hour':
        key = date.getHours().toString();
        break;
      case 'day':
        key = date.toISOString().split('T')[0];
        break;
      case 'week':
        const weekDay = date.getDay() === 0 ? 6 : date.getDay() - 1;
        key = weekDay.toString();
        break;
      case 'month':
        key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        break;
      default:
        key = date.toISOString();
    }

    if (!aggregated[key]) {
      aggregated[key] = { sum: 0, count: 0, max: -Infinity, min: Infinity };
    }

    aggregated[key].sum += item.value;
    aggregated[key].count++;
    aggregated[key].max = Math.max(aggregated[key].max, item.value);
    aggregated[key].min = Math.min(aggregated[key].min, item.value);
  });

  // Convert to final values based on aggregation type
  const result: { [key: string]: number } = {};
  Object.entries(aggregated).forEach(([key, stats]) => {
    switch (aggregationType) {
      case 'sum':
        result[key] = stats.sum;
        break;
      case 'avg':
        result[key] = stats.sum / stats.count;
        break;
      case 'max':
        result[key] = stats.max;
        break;
      case 'min':
        result[key] = stats.min;
        break;
    }
  });

  return result;
};

/**
 * Create a blob URL for file preview
 */
export const createFilePreviewUrl = (file: File): string => {
  return URL.createObjectURL(file);
};

/**
 * Revoke a blob URL to free memory
 */
export const revokeFilePreviewUrl = (url: string) => {
  if (url && url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
};

/**
 * Parse and normalize date from various formats
 */
export const parseDate = (dateInput: string | Date): Date => {
  if (dateInput instanceof Date) return dateInput;
  return new Date(dateInput);
};

/**
 * Format date range for display
 */
export const formatDateRange = (
  startDate: string | Date,
  endDate: string | Date,
  locale: string = 'es-CL'
): string => {
  const start = parseDate(startDate);
  const end = parseDate(endDate);
  return `${start.toLocaleDateString(locale)} - ${end.toLocaleDateString(locale)}`;
};
